import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaLock } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AdminPanelNew from '@/pages/AdminPanelNew';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Claves de localStorage - SESIÓN PERMANENTE
const ADMIN_TOKEN_KEY = 'deisycfit_admin_token';
const ADMIN_USER_KEY = 'deisycfit_admin_user';

// EMAILS DE ADMIN AUTORIZADOS - Solo estos pueden acceder al panel
const AUTHORIZED_ADMIN_EMAILS = [
  'deisycp1424@gmail.com'
];

const AdminWrapper = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const authCheckDone = useRef(false);

  useEffect(() => {
    // Solo ejecutar una vez
    if (authCheckDone.current) return;
    authCheckDone.current = true;
    
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    console.log('[AdminWrapper] Starting auth check...');
    
    try {
      // SESIÓN PERMANENTE: Solo verificar localStorage
      // NO hay verificación de backend, NO hay expiración
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      const savedAdminUser = localStorage.getItem(ADMIN_USER_KEY);
      
      console.log('[AdminWrapper] Token exists:', !!adminToken);
      console.log('[AdminWrapper] User exists:', !!savedAdminUser);
      
      // Si no hay token o usuario, no está autenticado
      if (!adminToken || !savedAdminUser) {
        console.log('[AdminWrapper] No credentials in localStorage - redirect to login');
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      // Parsear el usuario guardado
      let parsedUser;
      try {
        parsedUser = JSON.parse(savedAdminUser);
        console.log('[AdminWrapper] Parsed user:', parsedUser?.email, 'role:', parsedUser?.role);
      } catch (e) {
        console.error('[AdminWrapper] Error parsing user - clearing corrupted data');
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
        setAuthenticated(false);
        setLoading(false);
        return;
      }

      // SESIÓN PERMANENTE: Si hay token y usuario con role admin, verificar email autorizado
      // La sesión NUNCA expira automáticamente - solo termina con logout manual
      if (adminToken && parsedUser && parsedUser.role === 'admin') {
        // VERIFICACIÓN EXTRA DE SEGURIDAD: El email debe estar en la lista de admins autorizados
        const emailLower = (parsedUser.email || '').toLowerCase();
        const isAuthorizedAdmin = AUTHORIZED_ADMIN_EMAILS.some(e => e.toLowerCase() === emailLower);
        
        if (!isAuthorizedAdmin) {
          console.log('[AdminWrapper] ❌ Email NOT in authorized admins list:', parsedUser.email);
          // Limpiar tokens de admin fraudulentos
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          localStorage.removeItem(ADMIN_USER_KEY);
          setAuthenticated(false);
          setLoading(false);
          return;
        }

        // ⚡ INTERCEPTOR GLOBAL: inyecta el token admin en TODAS las llamadas /api/
        // Requerido por el backend endurecido (get_admin_user verifica auth real).
        // Se registra una sola vez por sesión de admin.
        if (!window.__deisyfit_admin_axios_interceptor) {
          window.__deisyfit_admin_axios_interceptor = axios.interceptors.request.use((config) => {
            const url = config.url || '';
            const t = localStorage.getItem(ADMIN_TOKEN_KEY);
            if (t && url.includes('/api/')) {
              config.headers = config.headers || {};
              // No sobrescribir si otro sistema ya puso Authorization
              if (!config.headers.Authorization && !config.headers.authorization) {
                config.headers.Authorization = `Bearer ${t}`;
              }
            }
            return config;
          });
          console.log('[AdminWrapper] ✅ Axios admin auth interceptor registered');
        }

        console.log('[AdminWrapper] ✅ PERMANENT admin session active - granting access');
        setAdminUser(parsedUser);
        setAuthenticated(true);
        setLoading(false);
        return;
      }

      // Si llegamos aquí, el usuario no tiene role admin
      console.log('[AdminWrapper] ❌ User role is not admin:', parsedUser?.role);
      setAuthenticated(false);
      setLoading(false);
      
    } catch (error) {
      console.error('[AdminWrapper] General error:', error);
      setAuthenticated(false);
      setLoading(false);
    }
  };

  // Función para logout manual (la única forma de cerrar sesión)
  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setAuthenticated(false);
    setAdminUser(null);
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>
            <FaCog className="text-2xl text-white animate-spin" />
          </div>
          <p className="text-gray-400">Verificando acceso...</p>
        </motion.div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 rounded-2xl max-w-md"
          style={{ 
            background: 'rgba(var(--brand-primary-glow), 0.1)',
            border: '1px solid rgba(var(--brand-primary-glow), 0.3)'
          }}
        >
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), #FF69B4)' }}
          >
            <FaLock className="text-3xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Acceso Denegado</h2>
          <p className="text-gray-400 mb-6">
            No tienes una sesión de administrador activa.
            Por favor inicia sesión para acceder al panel.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-lg font-bold text-white"
            style={{ 
              background: 'linear-gradient(135deg, var(--brand-primary), #FF69B4)',
              boxShadow: '0 4px 20px rgba(var(--brand-primary-glow), 0.3)'
            }}
            data-testid="go-to-admin-login-btn"
          >
            Iniciar Sesión
          </button>
        </motion.div>
      </div>
    );
  }

  // Usuario autenticado como admin
  return <AdminPanelNew user={adminUser} onLogout={handleLogout} />;
};

export default AdminWrapper;
